// @ts-expect-error: No declarations for @ember/owner
import { type Owner, setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import type Item from 'lumen/utils/datamodels/total-revenue/item';
import safeDivide from 'lumen/utils/numeric/safe-divide';

/**
 * POJO-copy for the total-revenue/reference-item model
 * Can be used to represent an item we don't want to persist in the store
 */
export default class ReferenceItem {
  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  declare item: Item;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremiumChange() {
    return this.totalRevenueService.formatChange(this.nonRoomPremiumChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremiumRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.nonRoomPremiumRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNrOfRoomsChange() {
    return this.totalRevenueService.formatChange(this.nrOfRoomsChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNrOfRoomsRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.nrOfRoomsRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenueChange() {
    return this.totalRevenueService.formatChange(
      this.totalNonRoomRevenueChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalNonRoomRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalNonRoomRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalNonRoomRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueChange() {
    return this.totalRevenueService.formatChange(this.totalRevenueChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenueChange() {
    return this.totalRevenueService.formatChange(this.totalRoomRevenueChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalRoomRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRoomRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRoomRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremium() {
    return this.totalRevenueService.calculateNonRoomPremium(
      this.totalNonRoomRevenue,
      this.totalRoomRevenue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremiumChange() {
    return this.totalRevenueService.calculateChange(
      this.item.nonRoomPremium,
      this.nonRoomPremium,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremiumRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.nonRoomPremium,
      this.nonRoomPremium,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nrOfRoomsChange() {
    return this.totalRevenueService.calculateChange(
      this.item.nrOfRooms ?? 0,
      this.nrOfRooms ?? 0,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nrOfRoomsRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.nrOfRooms,
      this.nrOfRooms,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalNonRoomRevenueOrPAR,
      this.totalNonRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalNonRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNight() {
    return safeDivide(this.totalNonRoomRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalNonRoomRevenuePerRoomNight,
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalNonRoomRevenuePerRoomNight,
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalNonRoomRevenueOrPAR,
      this.totalNonRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalRevenueOrPAR,
      this.totalRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNight() {
    return safeDivide(this.totalRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalRevenuePerRoomNight,
      this.totalRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalRevenuePerRoomNight,
      this.totalRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalRevenueOrPAR,
      this.totalRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalRoomRevenueOrPAR,
      this.totalRoomRevenueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNight() {
    return safeDivide(this.totalRoomRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.item.totalRoomRevenuePerRoomNight,
      this.totalRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalRoomRevenuePerRoomNight,
      this.totalRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.item.totalRoomRevenueOrPAR,
      this.totalRoomRevenueOrPAR,
    );
  }

  constructor(
    public owner: Owner,
    public totalRevenue: number,
    public totalRoomRevenue: number,
    public totalNonRoomRevenue: number,
    public capacity: number | undefined,
    public nrOfRooms: number | undefined,
    public referenceType: string,
    public referenceFromDate: Dayjs,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setOwner(this, owner);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static create(
    owner: Owner,
    data: {
      capacity?: number;
      nrOfRooms?: number;
      referenceFromDate: Dayjs;
      referenceType: string;
      totalNonRoomRevenue: number;
      totalRevenue: number;
      totalRoomRevenue: number;
    },
  ) {
    return new ReferenceItem(
      owner,
      data.totalRevenue,
      data.totalRoomRevenue,
      data.totalNonRoomRevenue,
      data.capacity,
      data.nrOfRooms,
      data.referenceType,
      data.referenceFromDate,
    );
  }
}
